import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { trackCheckoutSuccess, trackPurchase } from '../utils/analytics';
import API from '../utils/api';
import { shouldUseIap } from '../utils/iap';

// ── Plan feature definitions ───────────────────────────────────────────────────
const PLAN_FEATURES = {
  basic: {
    name:       'Basic',
    emoji:      '✨',
    color:      '#6f42c1',
    colorLight: '#f0e8ff',
    colorMid:   '#c9a8ff',
    tagline:    'Unlimited stories for your little one',
    highlights: [
      { icon: '📖', label: '60 stories every month',           sub: 'Up from 5 on the free plan' },
      { icon: '🔊', label: '30 AI narrations every month',     sub: 'Listen to every story read aloud' },
      { icon: '🎨', label: '20 illustration regenerations',    sub: 'Refresh artwork until it\'s perfect' },
      { icon: '📸', label: '3 photo illustrations per month',  sub: 'Face-accurate art of your child' },
      { icon: '👶', label: 'Up to 3 child profiles',           sub: 'Personalised stories for each child' },
      { icon: '📚', label: 'Full story library',               sub: 'Every story saved forever' },
      { icon: '🌍', label: '11 languages supported',           sub: 'Perfect for bilingual families' },
    ],
    nextSteps: [
      { icon: '✨', label: 'Create your first story',    sub: 'Pick a theme and let the magic begin',  to: '/dashboard/generate',  cta: 'Generate Now',     primary: true  },
      { icon: '👶', label: 'Set up child profiles',      sub: 'Add their name, age, and interests',    to: '/dashboard/children',  cta: 'Add Children',     primary: false },
      { icon: '🏠', label: 'Go to your dashboard',       sub: 'See your credits and library',          to: '/dashboard',           cta: 'Open Dashboard',   primary: false },
    ],
  },
  family: {
    name:       'Family',
    emoji:      '🌟',
    color:      '#fd7e14',
    colorLight: '#fff3e0',
    colorMid:   '#ffc078',
    tagline:    'Everything your whole family needs',
    highlights: [
      { icon: '📖', label: '120 stories every month',          sub: 'Double the Basic plan allowance' },
      { icon: '🔊', label: '40 AI narrations every month',     sub: 'Every story read aloud, every night' },
      { icon: '🎙️', label: '5 voice clone narrations',         sub: 'Hear stories in your own voice' },
      { icon: '🎨', label: '40 illustration regenerations',    sub: 'Perfect artwork, every time' },
      { icon: '📸', label: '6 photo illustrations per month',  sub: 'Face-accurate art for every child' },
      { icon: '👨‍👩‍👧‍👦', label: 'Unlimited child profiles',          sub: 'Every child in the family included' },
      { icon: '📚', label: 'Full story library',               sub: 'Every story saved forever' },
      { icon: '🌍', label: '11 languages supported',           sub: 'Perfect for bilingual families' },
    ],
    nextSteps: [
      { icon: '🎙️', label: 'Clone your voice',          sub: 'Record 60 seconds in Settings',         to: '/dashboard/settings',  cta: 'Record Voice',     primary: true  },
      { icon: '👶', label: 'Add all your children',      sub: 'A profile for every child in the family', to: '/dashboard/children', cta: 'Add Children',    primary: false },
      { icon: '✨', label: 'Create your first story',    sub: 'Pick a theme and let the magic begin',  to: '/dashboard/generate',  cta: 'Generate Now',     primary: false },
    ],
  },
};

// ── Animated star burst (pure CSS, no deps) ───────────────────────────────────
const Stars = ({ color }) => {
  const stars = ['⭐', '✨', '🌟', '💫', '⭐', '✨', '🌟', '💫', '⭐', '✨', '🌟', '💫'];
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {stars.map((s, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            fontSize: `${12 + (i % 3) * 6}px`,
            left:  `${5 + (i * 8) % 90}%`,
            top:   `${10 + (i * 13) % 80}%`,
            opacity: 0.25,
            animation: `float-star-${i % 3} ${3 + (i % 4)}s ease-in-out infinite`,
            animationDelay: `${(i * 0.3) % 2}s`,
          }}
        >{s}</span>
      ))}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const CheckoutSuccessPage = () => {
  const [searchParams]          = useSearchParams();
  const navigate                = useNavigate();
  const { user, refreshUser }   = useAuth();

  const rawPlan    = searchParams.get('plan')    || 'basic';
  const billing    = searchParams.get('billing') || 'monthly';
  const plan       = PLAN_FEATURES[rawPlan] ? rawPlan : 'basic';
  const meta       = PLAN_FEATURES[plan];
  const isAnnual   = billing === 'annual';
  // Trial checkout: flagged in the success URL and/or reflected as a 'trialing'
  // subscription once the webhook lands.
  const isTrial      = searchParams.get('trial') === '1' || user?.subscription?.status === 'trialing';
  const trialEndDate = user?.subscription?.trialEnd || user?.subscription?.currentPeriodEnd;

  // ── Poll until webhook activates the subscription ─────────────────────────
  // Stripe redirects instantly; the webhook fires ~1–3 s later.
  const [confirmed, setConfirmed] = useState(false);
  const [polling,   setPolling]   = useState(true);
  const pollRef = useRef(null);
  const pollCount = useRef(0);
  const MAX_POLLS = 8; // 8 × 2 s = 16 s max

  useEffect(() => {
    const check = async () => {
      // An app purchase: ask the server to read it from RevenueCat directly
      // rather than only waiting for the webhook — which never arrives in a
      // usable form for a purchase it cannot map to an account. A no-op on the
      // server until REVENUECAT_SECRET_KEY is set; the webhook still applies.
      if (shouldUseIap()) {
        try { await API.post('/subscriptions/iap/sync'); } catch (_) {}
      }
      try {
        await refreshUser();
      } catch (_) {}
    };

    check(); // immediate first check

    pollRef.current = setInterval(async () => {
      pollCount.current += 1;
      await check();
      if (pollCount.current >= MAX_POLLS) {
        clearInterval(pollRef.current);
        setPolling(false);
      }
    }, 2000);

    return () => clearInterval(pollRef.current);
  }, []); // eslint-disable-line

  // Watch the user object for subscription activation ('active' or 'trialing')
  useEffect(() => {
    const st = user?.subscription?.status;
    if (user?.subscription?.plan === plan && (st === 'active' || st === 'trialing')) {
      setConfirmed(true);
      setPolling(false);
      clearInterval(pollRef.current);
      trackCheckoutSuccess(plan, billing);
      // Revenue is recognised when the trial converts, not at trial start.
      if (st !== 'trialing') {
        // Matches backend USD defaults in backend/utils/currency.js
        const PLAN_AMOUNTS = {
          basic:     { monthly: 14,  annual: 154 },
          family:    { monthly: 22,  annual: 242 },
        };
        const amounts = PLAN_AMOUNTS[plan] || PLAN_AMOUNTS.basic;
        trackPurchase(plan, billing, isAnnual ? amounts.annual : amounts.monthly);
      }
    }
  }, [user, plan]); // eslint-disable-line

  // Stop polling banner once confirmed or max polls reached
  const showPolling = polling && !confirmed;
  // Polling ran its full 16s and the subscription still is not active. Stripe
  // only redirects here after a successful payment, so the money HAS been taken
  // — what has not happened is activation on our side (a delayed or failed
  // webhook). The page used to claim "Your subscription is confirmed." with a
  // green tick in exactly this state, which is the one case where the claim is
  // false: the customer would arrive at a dashboard still showing free limits
  // with nothing to explain it. Say what is actually true instead.
  const activationPending = !polling && !confirmed;

  // Redirect away if no plan param
  useEffect(() => {
    if (!searchParams.get('plan')) {
      navigate('/dashboard', { replace: true });
    }
  }, []); // eslint-disable-line

  const accentColor = meta.color;
  const lightBg     = meta.colorLight;
  const midColor    = meta.colorMid;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      <Helmet>
        <title>Welcome to {meta.name}! | Dreamy Bedtime</title>
      </Helmet>

      {/* Floating star keyframes */}
      <style>{`
        @keyframes float-star-0 { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-12px) rotate(15deg); } }
        @keyframes float-star-1 { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-8px) rotate(-10deg); } }
        @keyframes float-star-2 { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-16px) rotate(20deg); } }
        @keyframes fade-up { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        .success-animate { animation: fade-up 0.5s ease both; }
      `}</style>

      {/* ── Hero header ───────────────────────────────────────────────────── */}
      <div
        style={{
          background: `linear-gradient(135deg, #2d1b69 0%, ${accentColor} 100%)`,
          padding: '56px 24px 48px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Stars color={accentColor} />

        {/* Plan badge */}
        <div
          className="success-animate"
          style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '100px',
            padding: '6px 18px',
            fontSize: '13px',
            fontWeight: 700,
            color: '#fff',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: '20px',
            backdropFilter: 'blur(4px)',
          }}
        >
          {isTrial ? 'Free Trial' : (isAnnual ? 'Annual Plan' : 'Monthly Plan')} · {meta.name}
        </div>

        {/* Big emoji */}
        <div className="success-animate" style={{ fontSize: '72px', lineHeight: 1, marginBottom: '16px', animationDelay: '0.1s' }}>
          🎉
        </div>

        {/* Headline */}
        <h1
          className="success-animate fw-bold"
          style={{
            fontFamily: "'Fredoka One', cursive",
            color: '#fff',
            fontSize: 'clamp(28px, 5vw, 42px)',
            margin: '0 0 12px',
            lineHeight: 1.2,
            animationDelay: '0.15s',
          }}
        >
          Welcome to {meta.name}! {meta.emoji}
        </h1>

        <p
          className="success-animate"
          style={{ color: 'rgba(255,255,255,0.8)', fontSize: '17px', margin: '0 auto 28px', maxWidth: '480px', lineHeight: 1.6, animationDelay: '0.2s' }}
        >
          {isTrial ? (
            <>
              {meta.tagline}. Your{' '}
              <strong style={{ color: '#a8f0c0' }}>free trial is active</strong>
              {trialEndDate
                ? <> — you won't be charged until {new Date(trialEndDate).toLocaleDateString()}.</>
                : <> — enjoy every premium feature, on us.</>}
            </>
          ) : (
            <>
              {meta.tagline}. Your subscription is{' '}
              {confirmed
                ? <strong style={{ color: '#a8f0c0' }}>active and ready to use.</strong>
                : showPolling
                  ? <span>being confirmed<span style={{ opacity: 0.7 }}>…</span></span>
                  : <span>taking a little longer than usual to activate — your payment went through.</span>
              }
            </>
          )}
        </p>

        {/* Confirmation pill */}
        <div
          className="success-animate"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: confirmed ? 'rgba(168,240,192,0.2)' : 'rgba(255,255,255,0.12)',
            border: `1px solid ${confirmed ? 'rgba(168,240,192,0.5)' : 'rgba(255,255,255,0.25)'}`,
            borderRadius: '100px',
            padding: '8px 20px',
            color: confirmed ? '#a8f0c0' : 'rgba(255,255,255,0.75)',
            fontSize: '14px',
            fontWeight: 600,
            animationDelay: '0.25s',
          }}
        >
          {confirmed
            ? <><i className="bi bi-check-circle-fill"></i> {isTrial ? 'Free trial active' : 'Subscription active'}</>
            : showPolling
              ? <><span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span> Confirming payment…</>
              : <><i className="bi bi-hourglass-split"></i> Payment received — finishing activation</>
          }
        </div>
      </div>

      {/* Activation still pending after the poll window — give the customer a
          way forward instead of leaving them to discover free limits later. */}
      {activationPending && (
        <div className="container" style={{ maxWidth: '640px', marginTop: '-12px' }}>
          <div className="rounded-4 p-3 text-center" style={{
            background: 'var(--yellow-section)', border: '1px solid var(--border-yellow)',
            color: 'var(--text-orange2)', fontSize: '14px',
          }}>
            <i className="bi bi-info-circle me-2"></i>
            Your payment was successful. Activation can take a minute to come through.
            <button
              className="btn btn-sm btn-link p-0 ms-2 fw-semibold"
              style={{ color: 'var(--text-orange2)', fontSize: '14px' }}
              onClick={() => window.location.reload()}
            >
              Check again
            </button>
            <div className="small mt-1" style={{ opacity: 0.85 }}>
              If it still hasn't appeared in a few minutes, contact us at support@dreamybedtime.com and we'll sort it out right away.
            </div>
          </div>
        </div>
      )}

      {/* ── Body ──────────────────────────────────────────────────────────── */}
      <div className="container py-5" style={{ maxWidth: '860px' }}>

        {/* ── What you unlocked ──────────────────────────────────────────── */}
        <div className="success-animate mb-5" style={{ animationDelay: '0.3s' }}>
          <h2
            className="fw-bold text-center mb-1"
            style={{ fontFamily: "'Fredoka One', cursive", color: 'var(--text-main)', fontSize: '26px' }}
          >
            Here's what you unlocked
          </h2>
          <p className="text-center text-muted small mb-4">
            Your {meta.name} plan includes all of the following every month
          </p>

          <div className="row g-3">
            {meta.highlights.map((h, i) => (
              <div key={i} className="col-12 col-sm-6 col-lg-4">
                <div
                  style={{
                    background: 'var(--card-bg)',
                    border: `1.5px solid ${lightBg}`,
                    borderRadius: '16px',
                    padding: '18px 20px',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '14px',
                    boxShadow: '0 2px 8px rgba(111,66,193,0.06)',
                    transition: 'box-shadow 0.2s',
                  }}
                >
                  <div
                    style={{
                      width: '44px', height: '44px', minWidth: '44px',
                      background: lightBg,
                      borderRadius: '12px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '22px',
                    }}
                  >
                    {h.icon}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: 'var(--text-main)', fontSize: '14px', lineHeight: 1.3, marginBottom: '3px' }}>
                      {h.label}
                    </div>
                    <div style={{ color: 'var(--text-muted2)', fontSize: '12px', lineHeight: 1.4 }}>
                      {h.sub}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Billing summary ────────────────────────────────────────────── */}
        <div
          className="success-animate mb-5"
          style={{
            background: `linear-gradient(135deg, #2d1b69, ${accentColor})`,
            borderRadius: '20px',
            padding: '28px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
            animationDelay: '0.35s',
          }}
        >
          <div>
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
              Your plan
            </div>
            <div style={{ color: '#fff', fontSize: '22px', fontWeight: 700, fontFamily: "'Fredoka One', cursive" }}>
              Dreamy Bedtime {meta.name} {meta.emoji}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginTop: '4px' }}>
              {isTrial
                ? `Free trial${trialEndDate ? ` · first charge ${new Date(trialEndDate).toLocaleDateString()}` : ''} · cancel anytime`
                : (isAnnual ? 'Billed annually · best value' : 'Billed monthly · cancel anytime')}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Link
              to="/dashboard/settings"
              style={{
                display: 'inline-block',
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '100px',
                padding: '8px 20px',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                textDecoration: 'none',
                backdropFilter: 'blur(4px)',
              }}
            >
              <i className="bi bi-gear me-2"></i>Manage Billing
            </Link>
          </div>
        </div>

        {/* ── Next steps ─────────────────────────────────────────────────── */}
        <div className="success-animate mb-5" style={{ animationDelay: '0.4s' }}>
          <h2
            className="fw-bold text-center mb-1"
            style={{ fontFamily: "'Fredoka One', cursive", color: 'var(--text-main)', fontSize: '26px' }}
          >
            Get started in seconds
          </h2>
          <p className="text-center text-muted small mb-4">
            Three quick steps to your first magical story
          </p>

          <div className="row g-3">
            {meta.nextSteps.map((step, i) => (
              <div key={i} className="col-12 col-md-4">
                <div
                  style={{
                    background: step.primary ? `linear-gradient(135deg, #2d1b69, ${accentColor})` : '#fff',
                    border: step.primary ? 'none' : `1.5px solid ${lightBg}`,
                    borderRadius: '20px',
                    padding: '28px 24px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: '12px',
                    boxShadow: step.primary ? `0 8px 32px rgba(111,66,193,0.3)` : '0 2px 8px rgba(111,66,193,0.06)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Step number */}
                  <div
                    style={{
                      position: 'absolute', top: '14px', right: '16px',
                      width: '22px', height: '22px',
                      background: step.primary ? 'rgba(255,255,255,0.15)' : lightBg,
                      borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: 700,
                      color: step.primary ? '#fff' : accentColor,
                    }}
                  >
                    {i + 1}
                  </div>

                  <div
                    style={{
                      width: '56px', height: '56px',
                      background: step.primary ? 'rgba(255,255,255,0.15)' : lightBg,
                      borderRadius: '16px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '26px',
                    }}
                  >
                    {step.icon}
                  </div>

                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: '15px',
                        color: step.primary ? '#fff' : '#2d1b69',
                        marginBottom: '6px',
                        lineHeight: 1.3,
                      }}
                    >
                      {step.label}
                    </div>
                    <div style={{ fontSize: '13px', color: step.primary ? 'rgba(255,255,255,0.75)' : '#6b7280', lineHeight: 1.5 }}>
                      {step.sub}
                    </div>
                  </div>

                  <Link
                    to={step.to}
                    style={{
                      marginTop: 'auto',
                      display: 'inline-block',
                      background: step.primary ? '#fff' : `linear-gradient(135deg, #2d1b69, ${accentColor})`,
                      color: step.primary ? accentColor : '#fff',
                      borderRadius: '100px',
                      padding: '9px 22px',
                      fontSize: '13px',
                      fontWeight: 700,
                      textDecoration: 'none',
                      boxShadow: step.primary ? '0 4px 16px rgba(0,0,0,0.15)' : `0 4px 16px rgba(111,66,193,0.3)`,
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    {step.cta} →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Tips section ───────────────────────────────────────────────── */}
        <div
          className="success-animate"
          style={{
            background: 'var(--card-bg)',
            border: `1.5px solid ${lightBg}`,
            borderRadius: '20px',
            padding: '28px 32px',
            animationDelay: '0.45s',
          }}
        >
          <div className="row align-items-center g-4">
            <div className="col-12 col-md-7">
              <div style={{ fontSize: '13px', fontWeight: 700, color: accentColor, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                💡 Pro tip
              </div>
              <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One', cursive", fontSize: '20px' }}>
                Set a bedtime reminder
              </h5>
              <p style={{ color: 'var(--text-muted2)', fontSize: '14px', lineHeight: 1.7, marginBottom: 0 }}>
                Head to Settings and enable bedtime reminders — we'll nudge you every evening at the time you choose
                so you never miss a story night. You can also set up push notifications to get them right on your phone.
              </p>
            </div>
            <div className="col-12 col-md-5 d-flex gap-2 flex-wrap">
              <Link
                to="/dashboard/settings"
                style={{
                  flex: 1,
                  background: lightBg,
                  color: accentColor,
                  borderRadius: '12px',
                  padding: '14px 16px',
                  fontSize: '13px',
                  fontWeight: 700,
                  textDecoration: 'none',
                  textAlign: 'center',
                  minWidth: '120px',
                }}
              >
                <div style={{ fontSize: '24px', marginBottom: '6px' }}>⏰</div>
                Set reminder
              </Link>
              <Link
                to="/dashboard/generate"
                style={{
                  flex: 1,
                  background: `linear-gradient(135deg, #2d1b69, ${accentColor})`,
                  color: '#fff',
                  borderRadius: '12px',
                  padding: '14px 16px',
                  fontSize: '13px',
                  fontWeight: 700,
                  textDecoration: 'none',
                  textAlign: 'center',
                  minWidth: '120px',
                  boxShadow: `0 4px 16px rgba(111,66,193,0.3)`,
                }}
              >
                <div style={{ fontSize: '24px', marginBottom: '6px' }}>✨</div>
                First story
              </Link>
            </div>
          </div>
        </div>

        {/* ── Footer note ─────────────────────────────────────────────────── */}
        <p className="text-center text-muted small mt-4">
          A confirmation email has been sent to your inbox. Questions?{' '}
          <a href="mailto:support@dreamybedtime.com" style={{ color: accentColor }}>
            Contact our support team
          </a>
          .
        </p>

      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
